// Partie HAUTE des écrans Journaux : liste chronologique des écritures du
// journal pour l'exercice actif, en colonnes Débit / Crédit (jamais Entrant/Sortant).
// Filtres : période (du/au) et recherche (tiers ou libellé).
// Journaux VT/AC : bouton « Régler » + indicateur de règlement sur la ligne du tiers.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, formatErreur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import DatePicker from "@/components/DatePicker";
import { Loader2, Search, Wallet, Printer, Mail } from "lucide-react";
import { EnvoiFactureBouton } from "@/components/EnvoiFactureBouton";

const fmtDateCourte = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
  } catch {
    return iso;
  }
};

const fmt = (n) => (n ? Math.round(n).toLocaleString("fr-FR") : "");
const aujourdHui = () => new Date().toISOString().slice(0, 10);

// Badge d'état de règlement (factures VT/AC uniquement).
function BadgeReglement({ statut }) {
  if (statut === "reglee") {
    return <span data-testid="badge-reglee" className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">Réglée</span>;
  }
  if (statut === "partielle") {
    return <span data-testid="badge-partielle" className="text-xs px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">Partiellement réglée</span>;
  }
  return null;
}

export default function JournalEcritures({ code, libelleJournal, refreshKey }) {
  const [exercices, setExercices] = useState([]);
  const [exercice, setExercice] = useState(null);
  const [debut, setDebut] = useState("");
  const [fin, setFin] = useState("");
  const [recherche, setRecherche] = useState("");
  const [data, setData] = useState(null);
  const [chargement, setChargement] = useState(true);

  // Dialogue de règlement (VT/AC)
  const sensReglement = code === "VT" ? "entree" : "sortie"; // VT = encaissement, AC = décaissement
  const [ligneReg, setLigneReg] = useState(null);
  const [journalReg, setJournalReg] = useState("banque");
  const [dateReg, setDateReg] = useState(aujourdHui());
  const [montantReg, setMontantReg] = useState("");
  const [moyenReg, setMoyenReg] = useState("Virement");
  const [enReglement, setEnReglement] = useState(false);

  useEffect(() => {
    api.get("/exercices").then(({ data }) => {
      setExercices(data || []);
      if (data?.length) setExercice(data[0].annee);
    }).catch(() => setExercices([]));
  }, []);

  const charger = useCallback(async () => {
    if (!exercice) return;
    setChargement(true);
    try {
      const { data } = await api.get(`/journaux/${code}/ecritures`, {
        params: { exercice, debut: debut || undefined, fin: fin || undefined, recherche: recherche || undefined },
      });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setChargement(false);
    }
  }, [code, exercice, debut, fin, recherche]);

  useEffect(() => { charger(); }, [charger, refreshKey]);

  const ouvrirReglement = (l) => {
    const reste = Math.max((l.montant_ttc || 0) - (l.montant_regle || 0), 0);
    setLigneReg(l);
    setJournalReg("banque");
    setDateReg(aujourdHui());
    setMontantReg(String(reste || l.montant_ttc || ""));
    setMoyenReg("Virement");
  };

  const validerReglement = async () => {
    const montant = Math.round(parseFloat(montantReg) || 0);
    if (montant <= 0) { toast.error("Le montant du règlement doit être supérieur à zéro."); return; }
    setEnReglement(true);
    try {
      await api.post("/tresorerie/mouvements", {
        journal: journalReg,
        sens: sensReglement,
        date_mouvement: dateReg,
        montant,
        description: `Règlement ${ligneReg.numero} - ${ligneReg.tiers_nom || ""}`.trim(),
        moyen_paiement: journalReg === "caisse" ? "Espèces" : moyenReg,
        contrepartie_type: "tiers",
        tiers_id: ligneReg.tiers_id,
        reference: ligneReg.numero,
        facture_id: ligneReg.facture_id,
      });
      toast.success("Règlement enregistré avec succès.");
      setLigneReg(null);
      charger();
    } catch (e) {
      toast.error(formatErreur(e.response?.data?.detail));
    } finally {
      setEnReglement(false);
    }
  };

  return (
    <div className="bg-white border border-[#4A8A8A]/20 rounded-lg shadow-sm p-5" data-testid={`journal-liste-${code}`}>
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h2 className="text-lg font-bold text-[#1D3D3D]">Écritures du journal {libelleJournal}</h2>
        <div className="flex items-end gap-2 flex-wrap">
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-[#4A8A8A]">Exercice</Label>
            <Select value={exercice ? String(exercice) : ""} onValueChange={(v) => setExercice(parseInt(v, 10))}>
              <SelectTrigger data-testid={`journal-exercice-${code}`} className="h-9 w-28 bg-white border-[#4A8A8A]/30"><SelectValue /></SelectTrigger>
              <SelectContent>
                {exercices.map((e) => (<SelectItem key={e.annee} value={String(e.annee)}>{e.annee}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-[#4A8A8A]">Du</Label>
            <DatePicker testId={`journal-debut-${code}`} value={debut} onChange={setDebut} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-[#4A8A8A]">Au</Label>
            <DatePicker testId={`journal-fin-${code}`} value={fin} onChange={setFin} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs font-semibold text-[#4A8A8A]">Recherche</Label>
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-[#4A8A8A]" />
              <Input data-testid={`journal-recherche-${code}`} value={recherche} onChange={(e) => setRecherche(e.target.value)}
                placeholder="Tiers ou libellé" className="h-9 pl-8 w-44 bg-white border-[#4A8A8A]/30" />
            </div>
          </div>
        </div>
      </div>

      {chargement ? (
        <div className="flex justify-center py-8"><Loader2 className="h-6 w-6 animate-spin text-[#4A8A8A]" /></div>
      ) : !data?.lignes?.length ? (
        <p data-testid={`journal-vide-${code}`} className="py-8 text-center text-sm text-[#1D3D3D]/60">Aucune écriture sur cette période.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-[#4A8A8A] border-b border-[#4A8A8A]/20">
                <th className="py-2 pr-3">Date</th>
                <th className="py-2 pr-3">N° pièce</th>
                <th className="py-2 pr-3">Compte / Libellé</th>
                <th className="py-2 pr-3 text-right">Débit</th>
                <th className="py-2 pr-3 text-right">Crédit</th>
                <th className="py-2 text-center">Statut</th>
              </tr>
            </thead>
            <tbody data-testid={`journal-lignes-${code}`}>
              {data.lignes.map((l) => (
                <tr key={l.id} className="border-b border-[#4A8A8A]/10 hover:bg-[#E8F4F4]/40">
                  <td className="py-2 pr-3 whitespace-nowrap">{l.date}</td>
                  <td className="py-2 pr-3 font-mono text-xs">{l.numero}</td>
                  <td className="py-2 pr-3"><span className="font-mono text-xs text-[#4A8A8A]">{l.compte}</span> <span className="text-[#1D3D3D]">{l.libelle}</span></td>
                  <td className="py-2 pr-3 text-right tabular-nums text-[#1D3D3D]">{fmt(l.debit)}</td>
                  <td className="py-2 pr-3 text-right tabular-nums text-[#1D3D3D]">{fmt(l.credit)}</td>
                  <td className="py-2 text-center">
                    {l.reglable ? (
                      <div className="flex items-center justify-center gap-2 flex-wrap">
                        <BadgeReglement statut={l.statut_reglement} />
                        {code === "VT" && l.facture_id && (
                          <button type="button" data-testid={`imprimer-facture-${l.numero}`}
                            onClick={() => window.open(`${API}/factures/${l.facture_id}/pdf`, "_blank")}
                            title="Imprimer la facture"
                            className="h-7 w-7 rounded-md flex items-center justify-center text-[#1D3D3D] hover:bg-[#E8F4F4] transition-colors">
                            <Printer className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {code === "VT" && l.facture_id && (
                          <EnvoiFactureBouton factureId={l.facture_id} numero={l.numero} onSent={charger} />
                        )}
                        {code === "VT" && l.dernier_envoi && (
                          <span data-testid={`badge-envoyee-${l.numero}`}
                            title={`Envoyée le ${fmtDateCourte(l.dernier_envoi)}`}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 text-[11px] font-semibold">
                            <Mail className="h-3 w-3" /> Envoyée le {fmtDateCourte(l.dernier_envoi)}
                          </span>
                        )}
                        {l.statut_reglement !== "reglee" && (
                          <Button data-testid={`bouton-regler-${l.numero}`} size="sm" onClick={() => ouvrirReglement(l)}
                            className="h-7 px-2 bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white text-xs">
                            <Wallet className="h-3.5 w-3.5 mr-1" /> Régler
                          </Button>
                        )}
                      </div>
                    ) : (
                      <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">{l.statut}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="font-bold text-[#1D3D3D] border-t-2 border-[#4A8A8A]/30">
                <td className="py-2 pr-3" colSpan={3}>Total</td>
                <td data-testid={`journal-total-debit-${code}`} className="py-2 pr-3 text-right tabular-nums">{fmt(data.total_debit)}</td>
                <td data-testid={`journal-total-credit-${code}`} className="py-2 pr-3 text-right tabular-nums">{fmt(data.total_credit)}</td>
                <td className="py-2 text-center text-xs">{data.equilibre ? <span className="text-emerald-600">Équilibré</span> : <span className="text-red-600">Écart</span>}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}

      {/* Dialogue de règlement (VT/AC) */}
      <Dialog open={!!ligneReg} onOpenChange={(o) => !o && setLigneReg(null)}>
        <DialogContent data-testid="dialog-reglement">
          <DialogHeader>
            <DialogTitle>{code === "VT" ? "Encaisser" : "Régler"} la facture {ligneReg?.numero}</DialogTitle>
            <DialogDescription>
              {ligneReg?.tiers_nom} — TTC {fmt(ligneReg?.montant_ttc)} XPF · Déjà réglé {fmt(ligneReg?.montant_regle)} XPF
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label className="text-sm font-semibold text-[#1D3D3D]">Journal de règlement</Label>
              <div className="grid grid-cols-2 gap-2">
                <button type="button" data-testid="reglement-journal-banque" onClick={() => setJournalReg("banque")}
                  className={`h-10 rounded-md text-sm font-semibold transition-colors ${journalReg === "banque" ? "bg-[#1D3D3D] text-white" : "bg-[#E8F4F4] text-[#1D3D3D]"}`}>Banque (BQ)</button>
                <button type="button" data-testid="reglement-journal-caisse" onClick={() => setJournalReg("caisse")}
                  className={`h-10 rounded-md text-sm font-semibold transition-colors ${journalReg === "caisse" ? "bg-[#1D3D3D] text-white" : "bg-[#E8F4F4] text-[#1D3D3D]"}`}>Caisse (CA)</button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-[#1D3D3D]">Date</Label>
                <DatePicker testId="reglement-date" value={dateReg} onChange={setDateReg} />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-[#1D3D3D]">Montant (XPF)</Label>
                <Input type="number" step="1" min="1" inputMode="numeric" data-testid="reglement-montant"
                  value={montantReg} onChange={(e) => setMontantReg(e.target.value)}
                  className="bg-white border-[#4A8A8A]/30" />
              </div>
            </div>
            {journalReg === "banque" && (
              <div className="space-y-1.5">
                <Label className="text-sm font-semibold text-[#1D3D3D]">Mode de règlement</Label>
                <Select value={moyenReg} onValueChange={setMoyenReg}>
                  <SelectTrigger data-testid="reglement-moyen" className="bg-white border-[#4A8A8A]/30 h-9"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {["Virement", "Chèque", "Carte bancaire", "Prélèvement"].map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="secondary" data-testid="reglement-annuler" onClick={() => setLigneReg(null)}
              className="bg-[#E8F4F4] text-[#1D3D3D] border border-[#4A8A8A]/30">Annuler</Button>
            <Button data-testid="reglement-valider" onClick={validerReglement} disabled={enReglement}
              className="bg-[#1D3D3D] hover:bg-[#2D5A5A] text-white">
              {enReglement ? <Loader2 className="h-4 w-4 animate-spin" /> : "Valider le règlement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
